type FormatItem = {
  title: string;
  summary: string;
  duration: string;
  tag: string;
};

type FormatSectionProps = {
  id: string;
  kicker: string;
  title: string;
  description: string;
  items: FormatItem[];
  layout: "list" | "grid";
  tone: "plain" | "tinted";
  icon: string;
};

export default function FormatSection({
  id,
  kicker,
  title,
  description,
  items,
  layout,
  tone,
  icon,
}: FormatSectionProps) {
  const backdrop = tone === "tinted" ? "bg-background-100" : "bg-background-50";

  return (
    <section id={id} className={`w-full px-4 md:px-6 py-14 md:py-20 ${backdrop}`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-9 md:mb-12">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center">
                <i className={`${icon} text-sm`} />
              </span>
              <p className="font-label text-xs tracking-[0.2em] text-primary-700">
                {kicker}
              </p>
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug mb-3">
              {title}
            </h2>
            <p className="text-sm md:text-base leading-7 text-foreground-600">
              {description}
            </p>
          </div>
          <a
            href="#entry"
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md border border-foreground-300 text-foreground-800 text-sm font-label whitespace-nowrap hover:border-foreground-800 transition-colors cursor-pointer self-start md:self-auto"
          >
            全部入口
            <i className="ri-arrow-right-line" />
          </a>
        </div>

        {layout === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {items.map((item) => (
              <a
                key={item.title}
                href="#entry"
                className="group flex flex-col h-full rounded-lg border border-background-200 bg-background-50 p-5 md:p-6 transition-colors hover:border-primary-300 cursor-pointer"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-flex items-center h-7 px-3 rounded-full bg-accent-100 text-accent-900 text-xs font-label whitespace-nowrap">
                    {item.tag}
                  </span>
                  <span className="text-xs text-foreground-500 font-label whitespace-nowrap">
                    {item.duration}
                  </span>
                </div>
                <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-700 transition-colors mb-2">
                  {item.title}
                </h3>
                <p className="text-sm leading-6 text-foreground-600 flex-1">
                  {item.summary}
                </p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-label text-primary-700">
                  看这条
                  <i className="ri-arrow-right-line" />
                </span>
              </a>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-3 md:gap-4">
            {items.map((item) => (
              <a
                key={item.title}
                href="#entry"
                className="group grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-6 items-start md:items-center rounded-lg border border-background-200 bg-background-50 p-5 md:p-6 transition-colors hover:border-primary-300 cursor-pointer"
              >
                <div className="md:col-span-8">
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-700 transition-colors">
                      {item.title}
                    </h3>
                    <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-label whitespace-nowrap">
                      {item.tag}
                    </span>
                  </div>
                  <p className="text-sm leading-6 text-foreground-600">
                    {item.summary}
                  </p>
                </div>
                <div className="md:col-span-4 flex items-center md:justify-end gap-4">
                  <span className="text-xs text-foreground-500 font-label whitespace-nowrap">
                    {item.duration}
                  </span>
                  <span className="w-9 h-9 rounded-full border border-background-300 flex items-center justify-center text-foreground-600 group-hover:bg-primary-500 group-hover:text-background-50 group-hover:border-primary-500 transition-colors">
                    <i className="ri-play-line" />
                  </span>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}